import type { SxProps, Theme } from '@mui/material';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { palette } from '../../../../assets/styles/themes/tokens';
import { aiAssistantEditorContext } from '../../../../state/AIAssistantEditorProvider';
import { notifyBugsnagHandledError } from '../../../../utils/bugsnagClient';
import { copyElementContent } from '../../../../utils/copyElementContent';
import { copyFormattedContent } from '../../../../utils/copyFormattedContent';
import AIAssistantIconButton from '../../Dialog/AIAssistantIconButton';

export const COPY_BUTTON_TEST_ID = 'text-editor-copy-button';

interface AIAssistantTextEditorCopyButtonProps {
	content?: string;
	/**
	 * The `text/plain` clipboard flavor. Without it the plain flavor is derived
	 * from `content` via a detached element, whose `innerText` falls back to
	 * `textContent` — so anything the markup renders rather than spells out (list
	 * numbers, most notably) is silently lost. Pass it whenever `content` is
	 * assembled markup rather than prose.
	 */
	plainText?: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
}

const AIAssistantTextEditorCopyButton: React.FC<AIAssistantTextEditorCopyButtonProps> = ({
	content: contentOverride,
	plainText,
	dataTestId = COPY_BUTTON_TEST_ID,
	sx,
}) => {
	const { t } = useTranslation();
	const { editorData } = useContext(aiAssistantEditorContext);
	const [isCopied, setIsCopied] = useState(false);

	const content = contentOverride ?? editorData.content;

	const handleCopy = async () => {
		try {
			if (plainText === undefined) {
				const temp = document.createElement('div');
				temp.innerHTML = content;
				await copyElementContent(temp);
			} else {
				await copyFormattedContent(content, plainText);
			}

			setIsCopied(true);

			// Reset icon after 5 seconds
			setTimeout(() => {
				setIsCopied(false);
			}, 5000);
		} catch (error) {
			notifyBugsnagHandledError(error as Error);
		}
	};

	const canCopy = Boolean(content);

	return (
		<AIAssistantIconButton
			data-testid={dataTestId}
			icon={isCopied ? 'check' : 'copy'}
			tooltip={t('Copy to clipboard')}
			onClick={handleCopy}
			disabled={!canCopy}
			sx={isCopied ? { color: palette.success.main } : sx}
		/>
	);
};

export default AIAssistantTextEditorCopyButton;
